import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Send, Shield, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM",
  "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM", "5:00 PM"
];

const initialForm = {
  service_type: "",
  description: "",
  contact_method: "",
  full_name: "",
  company_name: "",
  phone: "",
  phone_type: "whatsapp",
  email: "",
  preferred_date: null,
  preferred_time: "",
  privacy_agreed: false,
};

export default function BookingForm({ preselectedCaseType, preselectedAttorney }) {
  const { t } = useLanguage();
  const bk = t("booking");
  const practiceItems = t("practiceAreas.items") || [];
  const errorMsgs = bk.errors || {};
  const contactMethods = bk.contactMethods || ["Teléfono", "Correo", "WhatsApp"];

  const [form, setForm] = useState({
    ...initialForm,
    service_type: preselectedCaseType || "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const errs = {};
    if (!form.service_type) errs.service_type = errorMsgs.caseType;
    if (!form.description.trim()) errs.description = errorMsgs.description;
    if (!form.contact_method) errs.contact_method = errorMsgs.contactMethod;
    if (!form.full_name.trim()) errs.full_name = errorMsgs.fullName;
    if (!form.phone.trim()) errs.phone = errorMsgs.phone;
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) errs.email = errorMsgs.email;
    if (!form.preferred_date) errs.preferred_date = errorMsgs.date;
    if (!form.preferred_time) errs.preferred_time = errorMsgs.time;
    if (!form.privacy_agreed) errs.privacy_agreed = errorMsgs.privacy;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/diagnostic-requests`, {
        ...form,
        preferred_date: form.preferred_date ? format(form.preferred_date, "yyyy-MM-dd") : "",
      });
      toast.success(t("consultation.successMsg"), { duration: 5000 });
      setForm({ ...initialForm });
    } catch {
      toast.error(t("consultation.errorMsg"));
    } finally {
      setSubmitting(false);
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const inputClass = "rounded-xl border-[#d5d8d3] bg-[#faf8f4] focus:border-[#103F38] focus:ring-[#103F38] h-12 text-[#152238]";

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="booking-form">
      <div>
        <label className="block text-sm font-medium text-[#152238] mb-2">{bk.caseType} *</label>
        <Select value={form.service_type} onValueChange={(v) => updateField("service_type", v)}>
          <SelectTrigger data-testid="booking-case-type" className={inputClass}><SelectValue placeholder={bk.caseTypePlaceholder} /></SelectTrigger>
          <SelectContent>
            {practiceItems.map((area) => (
              <SelectItem key={area.id} value={area.title}>{area.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.service_type && <p className="text-red-500 text-xs mt-1">{errors.service_type}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-[#152238] mb-2">{bk.caseDescription} *</label>
        <Textarea data-testid="booking-description" value={form.description} onChange={(e) => updateField("description", e.target.value)} placeholder={bk.caseDescPlaceholder} className="rounded-xl border-[#d5d8d3] bg-[#faf8f4] focus:border-[#103F38] focus:ring-[#103F38] min-h-[100px] text-[#152238]" />
        {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-[#152238] mb-2">{bk.contactMethod} *</label>
        <Select value={form.contact_method} onValueChange={(v) => updateField("contact_method", v)}>
          <SelectTrigger data-testid="booking-contact-method" className={inputClass}><SelectValue placeholder={bk.contactMethodPlaceholder} /></SelectTrigger>
          <SelectContent>
            {contactMethods.map((method) => (
              <SelectItem key={method} value={method}>{method}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.contact_method && <p className="text-red-500 text-xs mt-1">{errors.contact_method}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[#152238] mb-2">{bk.fullName} *</label>
          <Input data-testid="booking-full-name" value={form.full_name} onChange={(e) => updateField("full_name", e.target.value)} placeholder="Tu nombre completo" className={inputClass} />
          {errors.full_name && <p className="text-red-500 text-xs mt-1">{errors.full_name}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-[#152238] mb-2">Empresa</label>
          <Input data-testid="booking-company" value={form.company_name} onChange={(e) => updateField("company_name", e.target.value)} placeholder="Nombre de tu empresa" className={inputClass} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[#152238] mb-2">{t("consultation.labels.phone")} *</label>
          <Input
            data-testid="booking-phone"
            value={form.phone}
            onChange={(e) => updateField("phone", e.target.value)}
            placeholder={bk.phonePlaceholder}
            className={inputClass}
          />
          {/* Selector de tipo de teléfono */}
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              data-testid="phone-type-whatsapp"
              onClick={() => updateField("phone_type", "whatsapp")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-200 ${
                form.phone_type === "whatsapp"
                  ? "bg-[#25D366] border-[#25D366] text-white shadow-sm"
                  : "border-[#d5d8d3] text-[#66727B] hover:border-[#25D366] hover:text-[#25D366]"
              }`}
            >
              <MessageCircle className="h-3.5 w-3.5" />
              WhatsApp
            </button>
            <button
              type="button"
              data-testid="phone-type-telefono"
              onClick={() => updateField("phone_type", "telefono")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-200 ${
                form.phone_type === "telefono"
                  ? "bg-[#103F38] border-[#103F38] text-white shadow-sm"
                  : "border-[#d5d8d3] text-[#66727B] hover:border-[#103F38] hover:text-[#103F38]"
              }`}
            >
              <Phone className="h-3.5 w-3.5" />
              Solo llamadas
            </button>
          </div>
          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-[#152238] mb-2">{t("consultation.labels.email")} *</label>
          <Input data-testid="booking-email" type="email" value={form.email} onChange={(e) => updateField("email", e.target.value)} placeholder={bk.emailPlaceholder} className={inputClass} />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[#152238] mb-2">{bk.preferredDate} *</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button data-testid="booking-date-trigger" variant="outline" className={`w-full justify-start text-left font-normal ${inputClass}`}>
                <CalendarIcon className="mr-2 h-4 w-4" />
                {form.preferred_date ? format(form.preferred_date, "PPP") : bk.pickDate}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={form.preferred_date} onSelect={(date) => updateField("preferred_date", date)} disabled={(date) => date < new Date()} data-testid="booking-calendar" />
            </PopoverContent>
          </Popover>
          {errors.preferred_date && <p className="text-red-500 text-xs mt-1">{errors.preferred_date}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-[#152238] mb-2">{bk.preferredTime} *</label>
          <Select value={form.preferred_time} onValueChange={(v) => updateField("preferred_time", v)}>
            <SelectTrigger data-testid="booking-time" className={inputClass}><SelectValue placeholder={bk.selectTime} /></SelectTrigger>
            <SelectContent>
              {TIME_SLOTS.map((slot) => (<SelectItem key={slot} value={slot}>{slot}</SelectItem>))}
            </SelectContent>
          </Select>
          {errors.preferred_time && <p className="text-red-500 text-xs mt-1">{errors.preferred_time}</p>}
        </div>
      </div>

      <div className="flex items-start gap-3">
        <Checkbox
          id="privacy"
          data-testid="booking-privacy-checkbox"
          checked={form.privacy_agreed}
          onCheckedChange={(checked) => updateField("privacy_agreed", checked)}
          className="mt-1 border-[#d5d8d3] data-[state=checked]:bg-[#103F38] data-[state=checked]:border-[#103F38]"
        />
        <label htmlFor="privacy" className="text-sm text-[#66727B] cursor-pointer leading-relaxed">{bk.privacyText}</label>
      </div>
      {errors.privacy_agreed && <p className="text-red-500 text-xs">{errors.privacy_agreed}</p>}

      <Button
        type="submit"
        data-testid="booking-submit-btn"
        disabled={submitting}
        className="w-full bg-[#103F38] text-white hover:bg-[#0c332d] rounded-full px-8 py-6 text-base font-semibold shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50"
      >
        {submitting ? bk.submitting : (<><Send className="h-4 w-4 mr-2" />{bk.submitBtn}</>)}
      </Button>

      <div className="flex items-center justify-center gap-2 text-xs text-[#a09890]">
        <Shield className="h-3 w-3" />
        <span>{bk.confidentialNote}</span>
      </div>
    </form>
  );
}
